import { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { isPremium, startProCheckout } from "../lib/premium";
import { toast } from "sonner";
import { QrCode, RefreshCw, ShieldOff, Loader2, Gem, ClipboardCopy, CalendarDays, HeartPulse } from "lucide-react";

const ROLES = [
  { key: "asistente", label: "Asistente", icon: CalendarDays, path: "asistente", desc: "Administra tu agenda y citas (se sincronizan a Google Calendar). No ve datos clínicos." },
  { key: "enfermeria", label: "Enfermería", icon: HeartPulse, path: "enfermeria", desc: "Captura signos vitales y somatometría que se autollenan en tu consulta. No ve la historia clínica." },
];

export default function DelegateAccessCard() {
  const { user } = useAuth();
  const premium = isPremium(user) || user?.has_team || user?.role === "admin";
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = () => { api.get("/delegates").then((r) => setData(r.data || {})).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { if (premium) load(); else setLoading(false); }, [premium]);

  const generate = async (role) => { setBusy(role); try { const r = await api.post(`/delegates/${role}/generate`); setData((d) => ({ ...d, [role]: r.data })); toast.success("Código generado"); } catch { toast.error("Error"); } finally { setBusy(""); } };
  const revoke = async (role) => { if (!window.confirm("¿Revocar el acceso? Los enlaces actuales dejarán de funcionar.")) return; setBusy(role); try { await api.post(`/delegates/${role}/revoke`); setData((d) => ({ ...d, [role]: { ...d[role], active: false } })); toast.success("Acceso revocado"); } catch { toast.error("Error"); } finally { setBusy(""); } };
  const urlFor = (role) => { const d = data[role]; return d?.token ? `${window.location.origin}/${role}/${d.token}` : ""; };

  return (
    <div className="bg-white rounded-3xl p-6 border border-stone-100 shadow-[0_8px_30px_rgb(61,64,91,0.04)]" data-testid="delegate-access-card">
      <div className="flex items-center gap-2 mb-1">
        <QrCode size={20} className="text-brand-sage" />
        <h2 className="font-outfit font-bold text-lg text-brand-ink">Accesos delegados (QR)</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-5">Comparte un QR + PIN para que tu asistente o enfermera trabajen desde su propio teléfono, sin ver la historia clínica.</p>

      {!premium ? (
        <button data-testid="delegate-upgrade" onClick={startProCheckout} className="w-full flex items-center justify-center gap-2 bg-brand-terracotta text-white font-semibold py-3 rounded-2xl">
          <Gem size={16} /> Función Plan Pro — Actualizar
        </button>
      ) : loading ? (
        <div className="grid place-items-center py-8"><Loader2 className="animate-spin text-brand-sage" /></div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {ROLES.map((r) => {
            const d = data[r.key];
            const active = d?.active;
            const url = urlFor(r.key);
            return (
              <div key={r.key} className="border border-stone-200 rounded-2xl p-4" data-testid={`delegate-${r.key}`}>
                <div className="flex items-center gap-2 mb-1"><r.icon size={18} className="text-brand-ink" /><p className="font-outfit font-bold text-brand-ink">{r.label}</p></div>
                <p className="text-xs text-muted-foreground mb-3">{r.desc}</p>
                {active && d?.token ? (
                  <div className="flex flex-col items-center gap-3">
                    <div className="bg-white p-2 rounded-xl border"><QRCodeCanvas value={url} size={132} fgColor="#1E293B" level="M" /></div>
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">PIN</p>
                      <p data-testid={`delegate-pin-${r.key}`} className="font-outfit font-extrabold text-2xl tracking-[0.3em] text-brand-ink">{d.pin}</p>
                    </div>
                    <button onClick={() => { navigator.clipboard.writeText(url); toast.success("Enlace copiado"); }} className="text-xs font-semibold text-brand-sage flex items-center gap-1"><ClipboardCopy size={13} /> Copiar enlace</button>
                    <div className="flex gap-2 w-full">
                      <button data-testid={`delegate-regen-${r.key}`} onClick={() => generate(r.key)} disabled={busy === r.key} className="flex-1 text-xs font-bold border border-stone-200 rounded-xl py-2 flex items-center justify-center gap-1 disabled:opacity-60"><RefreshCw size={13} /> Regenerar</button>
                      <button data-testid={`delegate-revoke-${r.key}`} onClick={() => revoke(r.key)} disabled={busy === r.key} className="flex-1 text-xs font-bold text-red-600 border border-red-200 rounded-xl py-2 flex items-center justify-center gap-1"><ShieldOff size={13} /> Revocar</button>
                    </div>
                  </div>
                ) : (
                  <button data-testid={`delegate-generate-${r.key}`} onClick={() => generate(r.key)} disabled={busy === r.key}
                    className="w-full bg-brand-sage text-white text-sm font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60">
                    {busy === r.key ? <Loader2 size={15} className="animate-spin" /> : <QrCode size={15} />} Generar acceso
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
